import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUpdateOneOnOne } from '@/shared/hooks/useOneOnOne';
import type { GoalsReviewData } from '../types/wizard.types';
import { OneOnOneStatus } from '@momentum/types';

/**
 * Hook to save goals review stage data
 * This doesn't need to make an API call as it's just intermediate state
 */
export const useSaveGoalsReview = () => {
  return useMutation({
    mutationFn: async (data: GoalsReviewData) => {
      // Just return the data - we'll save everything on final submit
      console.log('[Goals Review] Saved goals review data:', data);
      return data;
    },
    onSuccess: () => {
      console.log('[Goals Review] Goals review stage completed');
    },
    onError: (error) => {
      console.error('[Goals Review] Failed to save goals review:', error);
    },
  });
};

/**
 * Hook to submit the entire one-on-one session
 * Updates the one-on-one status to COMPLETED and saves notes
 */
export const useSubmitOneOnOne = (sessionId: string) => {
  const updateOneOnOneMutation = useUpdateOneOnOne();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (meetingNotes?: string[]) => {
      console.log('[One-on-One] Submitting session:', sessionId);
      console.log('[One-on-One] Meeting notes:', meetingNotes);

      // Update the one-on-one to COMPLETED status with notes
      const result = await updateOneOnOneMutation.mutateAsync({
        id: sessionId,
        data: {
          status: OneOnOneStatus.COMPLETED,
          notes: meetingNotes
            ? {
                manager: meetingNotes,
              }
            : undefined,
        },
      });

      console.log('[One-on-One] Session submitted successfully');
      return result;
    },
    onSuccess: () => {
      // Invalidate any queries that might be affected
      queryClient.invalidateQueries({ queryKey: ['one-on-one'] });
      queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
    onError: (error) => {
      console.error('[One-on-One] Failed to submit session:', error);
      throw error;
    },
  });
};
